import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { COMPANY, MEDIA } from "@/data/content";
import { Checkbox } from "@/components/ui/checkbox";

const empty = { nome: "", email: "", telefono: "", oggetto: "", messaggio: "", privacy: false };

export default function Contatti() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.nome.trim().length < 2) e.nome = "Inserisci un nome valido";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Inserisci un'email valida";
    if (form.oggetto.trim().length < 2) e.oggetto = "Inserisci un oggetto";
    if (form.messaggio.trim().length < 10) e.messaggio = "Il messaggio deve avere almeno 10 caratteri";
    if (!form.privacy) e.privacy = "Devi accettare l'informativa sulla privacy";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Richiesta inviata", {
        description: "Grazie. Un consulente XL Brokers ti contatterà nelle prossime 24 ore lavorative.",
      });
      setForm(empty);
    } catch (e) {
      toast.error("Errore", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contatti" className="pt-28">
      <section className="container-x py-16 sm:py-24 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <span className="overline">Contatti</span>
        </div>
        <div className="md:col-span-8">
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight">
            Scrivici.<br />
            <span className="italic text-primary">Rispondiamo</span> sempre.
          </h1>
          <p className="mt-8 lead max-w-2xl">
            Per una consulenza riservata, un preventivo, una richiesta di sinistro o semplicemente per conoscerci. Compila il modulo o utilizza i nostri recapiti.
          </p>
        </div>
      </section>

      <section className="container-x pb-32 grid lg:grid-cols-12 gap-16">
        <form onSubmit={submit} className="lg:col-span-7 space-y-8" data-testid="contact-form" noValidate>
          <div className="grid sm:grid-cols-2 gap-8">
            <div>
              <label htmlFor="nome" className="field-label">Nome e cognome*</label>
              <input
                id="nome"
                data-testid="contact-input-nome"
                value={form.nome}
                onChange={(e) => update("nome", e.target.value)}
                className="field-input"
                placeholder="Mario Rossi"
              />
              {errors.nome && <p className="mt-2 text-xs text-destructive" data-testid="error-nome">{errors.nome}</p>}
            </div>
            <div>
              <label htmlFor="email" className="field-label">Email*</label>
              <input
                id="email"
                type="email"
                data-testid="contact-input-email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="field-input"
                placeholder="nome@azienda.it"
              />
              {errors.email && <p className="mt-2 text-xs text-destructive" data-testid="error-email">{errors.email}</p>}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-8">
            <div>
              <label htmlFor="telefono" className="field-label">Telefono</label>
              <input
                id="telefono"
                data-testid="contact-input-telefono"
                value={form.telefono}
                onChange={(e) => update("telefono", e.target.value)}
                className="field-input"
                placeholder="+39 ..."
              />
            </div>
            <div>
              <label htmlFor="oggetto" className="field-label">Oggetto*</label>
              <input
                id="oggetto"
                data-testid="contact-input-oggetto"
                value={form.oggetto}
                onChange={(e) => update("oggetto", e.target.value)}
                className="field-input"
                placeholder="Es. Preventivo RC professionale"
              />
              {errors.oggetto && <p className="mt-2 text-xs text-destructive" data-testid="error-oggetto">{errors.oggetto}</p>}
            </div>
          </div>

          <div>
            <label htmlFor="messaggio" className="field-label">Messaggio*</label>
            <textarea
              id="messaggio"
              rows={6}
              data-testid="contact-input-messaggio"
              value={form.messaggio}
              onChange={(e) => update("messaggio", e.target.value)}
              className="field-input resize-none"
              placeholder="Raccontaci la tua attività e cosa cerchi..."
            />
            {errors.messaggio && <p className="mt-2 text-xs text-destructive" data-testid="error-messaggio">{errors.messaggio}</p>}
          </div>

          <div className="flex items-start gap-3 pt-2">
            <Checkbox
              id="privacy"
              data-testid="contact-checkbox-privacy"
              checked={form.privacy}
              onCheckedChange={(v) => update("privacy", !!v)}
              className="mt-1"
            />
            <label htmlFor="privacy" className="text-sm text-muted-foreground leading-relaxed">
              Ho letto e accetto l'informativa sulla privacy ai sensi dell'art. 13 del Regolamento UE 2016/679 (GDPR).*
            </label>
          </div>
          {errors.privacy && <p className="text-xs text-destructive" data-testid="error-privacy">{errors.privacy}</p>}

          <button type="submit" disabled={loading} data-testid="contact-submit-button" className="btn-primary mt-4">
            {loading ? "Invio in corso..." : "Invia richiesta"}
          </button>
        </form>

        <aside className="lg:col-span-5 space-y-12">
          <img src={MEDIA.contact} alt="Ufficio" className="w-full aspect-[4/5] object-cover" />
          <div className="space-y-8">
            <div>
              <p className="overline">Sede</p>
              <p className="mt-3 text-lg leading-relaxed">{COMPANY.address}</p>
            </div>
            <div>
              <p className="overline">Telefono</p>
              <a href={`tel:${COMPANY.phone}`} className="mt-3 inline-block font-display text-2xl underline-link" data-testid="contact-phone">
                {COMPANY.phone}
              </a>
            </div>
            <div>
              <p className="overline">Email</p>
              <a href={`mailto:${COMPANY.email}`} className="mt-3 inline-block font-display text-2xl underline-link" data-testid="contact-email">
                {COMPANY.email}
              </a>
            </div>
            <div>
              <p className="overline">PEC</p>
              <p className="mt-3 text-lg">{COMPANY.pec}</p>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
